import { renderStructuredText, stripHtmlTags } from "lib/prismic";
import { parseKeyValueLines } from "lib/key-value-lines";
import {
  CaptureType,
  PrismicCollectionDetailField,
  QrCaptureType,
} from "types";
import { LabelConfigurationError } from "lib/label-relations";

export interface TaskStepLabel {
  field: PrismicCollectionDetailField;
  value: string;
  displayName: string;
}

export interface TaskStep {
  slice: any;
  stepNumber: number;
  stepLabel: string;
  configuredStepLabel?: string;
  stepLabelDisplayName?: string;
  stepLabels?: TaskStepLabel[];
  stepTitle: string;
  numberOfCaptures: number;
  captureTaskId?: string;
  captureTaskStepNumber?: number;
  captureType?: CaptureType;
}

export function getStepLabel(
  step: Pick<TaskStep, "configuredStepLabel">,
  stepNumber: number
): string {
  return step.configuredStepLabel || `Step ${stepNumber}`;
}

function applyStepLabels(instructions: any, steps: TaskStep[]): TaskStep[] {
  const sources: PrismicCollectionDetailField[] = (
    instructions?.instruction_content || []
  )
    .filter((slice: any) => slice.slice_type === "collection_details")
    .flatMap((slice: any) => slice.items || [])
    .filter(
      (field: PrismicCollectionDetailField) => field.use_as_step_labels === true
    );
  if (sources.length === 0) return steps;
  const ids = new Set<string>();
  const labels = sources.map((field: PrismicCollectionDetailField) => {
    if (field.label_type !== "dropdown") {
      throw new LabelConfigurationError(
        "Use as step labels requires a dropdown label."
      );
    }
    let options;
    try {
      options = parseKeyValueLines(field.dropdown_options, {
        rejectInvalid: true,
        fieldName: "Step labels",
      });
    } catch (error) {
      throw new LabelConfigurationError(
        error instanceof Error ? error.message : "Invalid step label options."
      );
    }
    const id = field.label_id?.toString().trim();
    if (
      !id ||
      /[\s:|,]/.test(id) ||
      options.some(({ value }) => /[\s:|,]/.test(value))
    ) {
      throw new LabelConfigurationError(
        "Step label IDs and stored values cannot contain whitespace, colons, commas, or pipes."
      );
    }
    if (ids.has(id))
      throw new LabelConfigurationError(
        "Step label fields must have distinct IDs."
      );
    ids.add(id);
    if (new Set(options.map(({ value }) => value)).size !== options.length) {
      throw new LabelConfigurationError(
        "Step labels must have distinct stored values."
      );
    }
    return { field, options };
  });
  const combinationCount = labels.reduce(
    (count, label) => count * label.options.length,
    1
  );
  if (
    !Number.isSafeInteger(combinationCount) ||
    combinationCount === 0 ||
    combinationCount !== steps.length
  ) {
    throw new LabelConfigurationError(
      `Step label combination count (${combinationCount}) must match the capture task step count (${steps.length}).`
    );
  }
  // Preserve configured field/option order; the last dropdown changes fastest.
  return steps.map((step, index) => {
    let remainder = index;
    const stepLabels: TaskStepLabel[] = new Array(labels.length);
    for (let i = labels.length - 1; i >= 0; i--) {
      const { field, options } = labels[i];
      const option = options[remainder % options.length];
      remainder = Math.floor(remainder / options.length);
      stepLabels[i] = { field, value: option.value, displayName: option.key };
    }
    const stepLabel = stepLabels
      .map(
        ({ field, value }) => `${field.label_id?.toString().trim()}:${value}`
      )
      .join("\n");
    return {
      ...step,
      stepLabels,
      configuredStepLabel: stepLabel,
      stepLabel,
      stepLabelDisplayName: stepLabels
        .map(({ displayName }) => displayName)
        .join(" / "),
    };
  });
}

export function getTaskSteps(instructions: any): TaskStep[] {
  const steps = (instructions?.instruction_content || [])
    .filter((slice: any) => slice.slice_type === "step_instructions_")
    .map((slice: any, index: number) => ({
      slice,
      stepNumber: Number(slice.primary?.step_number) || index + 1,
      stepLabel: slice.primary?.step_label || `Step ${index + 1}`,
      configuredStepLabel: slice.primary?.step_label || undefined,
      stepTitle: stripHtmlTags(
        renderStructuredText(slice.primary?.step_title || "")
      ),
      numberOfCaptures: Number(slice.primary?.number_of_captures) || 1,
    }));
  return applyStepLabels(instructions, steps);
}

export function getOrbTaskSteps(
  instructions: any,
  captureType: QrCaptureType = "orb"
): TaskStep[] {
  const counts = (instructions?.instruction_content || [])
    .filter((slice: any) => slice.slice_type === "instruction_text___image")
    .map((slice: any) => slice.primary?.number_of_steps)
    .filter(
      (count: unknown) => count !== null && count !== undefined && count !== ""
    );
  const count = counts.length ? Number(counts[0]) : 1;
  if (
    !Number.isSafeInteger(count) ||
    count < 1 ||
    count > 1000 ||
    counts.some((value: unknown) => Number(value) !== count)
  ) {
    throw new LabelConfigurationError(
      `${
        captureType === "rtms" ? "RTMS" : "Orb"
      } number of steps must be a consistent whole number between 1 and 1000.`
    );
  }
  return applyStepLabels(
    instructions,
    Array.from({ length: count }, (_, index) => ({
      slice: null,
      stepNumber: index + 1,
      stepLabel: `Step ${index + 1}`,
      stepTitle:
        instructions?.task_name ||
        `${captureType === "rtms" ? "RTMS" : "Orb"} capture`,
      numberOfCaptures: 1,
      captureType,
    }))
  );
}

export function getTaskStepDetailFields(
  instructions: any,
  stepNumber: number
): PrismicCollectionDetailField[] {
  return (instructions?.instruction_content || [])
    .filter(
      (slice: any) =>
        slice.slice_type === "step_details" &&
        Number(slice.primary?.step_number) === stepNumber
    )
    .flatMap((slice: any) => slice.items || []);
}

function fieldSchema(field: PrismicCollectionDetailField) {
  return {
    id: field.label_id?.toString().trim() || "",
    type: field.label_type?.toString() || "text",
    required: Boolean(field.is_required),
    options: parseKeyValueLines(field.dropdown_options)
      .map(({ key, value }) => [key, value])
      .sort(([leftKey, leftValue], [rightKey, rightValue]) =>
        `${leftKey}:${leftValue}`.localeCompare(`${rightKey}:${rightValue}`)
      ),
  };
}

export function getStepSchemaFingerprint({
  captureType,
  numberOfCaptures,
  fields,
  stepLabel,
}: {
  captureType?: CaptureType;
  numberOfCaptures: number;
  fields: PrismicCollectionDetailField[];
  stepLabel?: string;
}): string {
  return JSON.stringify({
    version: 1,
    captureType: captureType || "",
    numberOfCaptures,
    ...(stepLabel ? { stepLabel } : {}),
    fields: fields
      .map(fieldSchema)
      .filter(({ id }) => id)
      .sort((left, right) => left.id.localeCompare(right.id)),
  });
}

export function getCollectionDetailsSchemaFingerprint(labels: unknown): string {
  const configured = (labels || {}) as Record<string, any>;
  const devices = Array.isArray(configured.devices)
    ? configured.devices
        .map((device: any) => ({
          key: String(device.key || ""),
          cameraNotFlipped: Boolean(device.camera_not_flipped),
        }))
        .filter(({ key }: { key: string }) => key)
        .sort((left: { key: string }, right: { key: string }) =>
          left.key.localeCompare(right.key)
        )
    : [];
  const fields = Object.entries(configured)
    .filter(([id, field]) => id !== "devices" && field && !Array.isArray(field))
    .map(([id, field]: [string, any]) => ({
      id,
      type: String(field.field_type || "text"),
      required: Boolean(field.required),
      options: Object.entries(field.labels || {}).sort(([left], [right]) =>
        left.localeCompare(right)
      ),
    }))
    .sort((left, right) => left.id.localeCompare(right.id));

  return JSON.stringify({ version: 1, devices, fields });
}

export function getStepRouteState(
  step: TaskStep,
  totalSteps: number,
  task: {
    taskId: string;
    taskName?: string;
    captureType?: string;
    editMode?: boolean;
  }
) {
  return {
    stepNumber: step.stepNumber,
    stepLabel: step.stepLabel,
    stepTitle: step.stepTitle,
    numberOfCaptures: step.numberOfCaptures,
    totalSteps,
    taskId: task.taskId,
    taskName: task.taskName,
    captureType: step.captureType || task.captureType,
    captureTaskId: step.captureTaskId,
    captureTaskStepNumber: step.captureTaskStepNumber,
    editMode: task.editMode || false,
  };
}
